function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import React, { forwardRef } from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { Loader2 } from 'lucide-react';
import { StyledButton, LoaderIcon, IconContainer } from './styles/button.styles';
import { defaultTheme } from '../../../types/theme';
/**
 * 통합된 버튼 컴포넌트
 * 
 * 아토믹 디자인에서 원자(Atom) 레벨 컴포넌트
 * 다양한 변형과 크기, 아이콘 지원
 * ActionButton 기능도 통합된 버전
 * 
 * @example
 * ```tsx
 * // 일반 버튼으로 사용
 * <Button variant="primary" size="md">버튼</Button>
 * <Button variant="outline" leftIcon={<HomeIcon />}>홈으로</Button>
 * <Button isLoading>로딩 중</Button>
 * 
 * // ActionButton으로 사용 (아이콘 전용 버튼)
 * <Button iconOnly icon={<PencilIcon />} variant="ghost" label="수정" />
 * ```
 */ var Button = /*#__PURE__*/ forwardRef(function(_param, ref) {
    var children = _param.children, className = _param.className, _param_variant = _param.variant, variant = _param_variant === void 0 ? 'primary' : _param_variant, _param_size = _param.size, size = _param_size === void 0 ? 'md' : _param_size, _param_fullWidth = _param.fullWidth, fullWidth = _param_fullWidth === void 0 ? false : _param_fullWidth, _param_shape = _param.shape, shape = _param_shape === void 0 ? 'pill' : _param_shape, _param_iconOnly = _param.iconOnly, iconOnly = _param_iconOnly === void 0 ? false : _param_iconOnly, _param_animation = _param.animation, animation = _param_animation === void 0 ? 'scale' : _param_animation, _param_isLoading = _param.isLoading, isLoading = _param_isLoading === void 0 ? false : _param_isLoading, leftIcon = _param.leftIcon, rightIcon = _param.rightIcon, icon = _param.icon, label = _param.label, disabled = _param.disabled, _param_useCustomLoader = _param.useCustomLoader, useCustomLoader = _param_useCustomLoader === void 0 ? false : _param_useCustomLoader, minWidth = _param.minWidth, props = _object_without_properties(_param, [
        "children",
        "className",
        "variant",
        "size",
        "fullWidth",
        "shape",
        "iconOnly",
        "animation",
        "isLoading",
        "leftIcon",
        "rightIcon",
        "icon",
        "label",
        "disabled",
        "useCustomLoader",
        "minWidth"
    ]);
    // iconOnly 모드일 때는 icon을 children으로 사용
    var buttonContent = iconOnly ? icon : children;
    // iconOnly 모드일 때는 항상 shape이 circular(rounded-full)이 되도록 설정
    var buttonShape = iconOnly ? 'circular' : shape;
    return /*#__PURE__*/ React.createElement(StyledButton, _object_spread({
        ref: ref,
        $variant: variant,
        $size: size,
        $fullWidth: fullWidth,
        $shape: buttonShape,
        $iconOnly: iconOnly,
        $animation: animation,
        disabled: isLoading || disabled,
        "aria-label": iconOnly ? label : undefined,
        className: "border-0 ".concat(className || ''),
        $minWidth: typeof minWidth === 'string' ? parseInt(minWidth) : minWidth,
        theme: defaultTheme
    }, props), isLoading && useCustomLoader ? /*#__PURE__*/ React.createElement(Loader2, {
        className: "mr-2 h-4 w-4 animate-spin"
    }) : isLoading ? /*#__PURE__*/ React.createElement(LoaderIcon, {
        xmlns: "http://www.w3.org/2000/svg",
        fill: "none",
        viewBox: "0 0 24 24"
    }, /*#__PURE__*/ React.createElement("circle", {
        className: "opacity-25",
        cx: "12",
        cy: "12",
        r: "10",
        stroke: "currentColor",
        strokeWidth: "4"
    }), /*#__PURE__*/ React.createElement("path", {
        className: "opacity-75",
        fill: "currentColor",
        d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    })) : null, !isLoading && !iconOnly && leftIcon && /*#__PURE__*/ React.createElement(IconContainer, {
        $position: "left"
    }, leftIcon), buttonContent, !isLoading && !iconOnly && rightIcon && /*#__PURE__*/ React.createElement(IconContainer, {
        $position: "right"
    }, rightIcon));
});
Button.displayName = 'Button';
export default Button;
